import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import {
  MonthlySolarReport,
  monthlySolarReportSchema,
} from '@sophocles/entities/MonthlySolarReport/monthlySolarReportRecord';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { fromJS } from 'immutable';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_MONTHLY_SOLAR_REPORTS = 'RECEIVE_MONTHLY_SOLAR_REPORTS';
function receiveMonthlySolarReports(json) {
  return {
    type: RECEIVE_MONTHLY_SOLAR_REPORTS,
    monthlySolarReports: mapListToRecords(json, MonthlySolarReport),
  };
}

/* Request Actions */
// starter request
export const REQUEST_MONTHLY_SOLAR_REPORT = 'REQUEST_MONTHLY_SOLAR_REPORT';
function requestMonthlySolarReport(monthlySolarReport_id) {
  return {
    type: REQUEST_MONTHLY_SOLAR_REPORT,
    monthlySolarReport_id,
  };
}

export const REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_WITH_RANGE = 'REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_WITH_RANGE';
function requestMonthlySolarReportsForSiteWithRange(site_id, from, to) {
  return {
    type: REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_WITH_RANGE,
    site_id,
    from,
    to,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchMonthlySolarReport(monthlySolarReport_id) {
  return dispatch => {
    dispatch(requestMonthlySolarReport(monthlySolarReport_id));
    return apiClient
      .get(`/monthlysolarreports/${monthlySolarReport_id}/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data, 'monthlySolarReports', monthlySolarReportSchema))
      .then(json => dispatch(receiveMonthlySolarReports(json.entities.monthlySolarReports)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_MONTHLY_SOLAR_REPORT)));
  };
}

export function fetchMonthlySolarReportsForSiteWithRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));
  return dispatch => {
    dispatch(requestMonthlySolarReportsForSiteWithRange(site_id, fromTS, toTS));
    return apiClient
      .get('/monthlysolarreports/', {
        params: {
          limit: 1000,
          site: site_id,
          month_end_date__gte: format(fromTS, 'yyyy-MM-dd'),
          month_end_date__lte: format(toTS, 'yyyy-MM-dd'),
        },
      })
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'monthlySolarReports', monthlySolarReportSchema))
      .then(json => dispatch(receiveMonthlySolarReports(json.entities.monthlySolarReports)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_WITH_RANGE)));
  };
}

// expecting a siteGroup id and a moment object for endDate
export function fetchMonthlySolarReportsForSiteGroupWithEndDate(siteGroup_id, endDate) {
  return dispatch => {
    dispatch({ type: 'REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_GROUP_WITH_RANGE', siteGroup_id, endDate });
    return apiClient
      .get('/monthlysolarreports/', {
        params: { limit: 1000, site__site_groups: siteGroup_id, month_end_date: endDate, exclude_wallscape: true },
      })
      .then(checkHttpStatus)
      .then(response => fromJS(response.data.results))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_MONTHLY_SOLAR_REPORTS_FOR_SITE_WITH_RANGE)));
  };
}

/* Edit Actions */
export const EDIT_MONTHLY_SOLAR_REPORT = 'EDIT_MONTHLY_SOLAR_REPORT';
export function editMonthlySolarReport(monthlySolarReport_id) {
  return {
    type: EDIT_MONTHLY_SOLAR_REPORT,
    monthlySolarReport: monthlySolarReport_id,
  };
}

export function patchMonthlySolarReport(monthlySolarReport_id, values) {
  return dispatch => {
    dispatch(editMonthlySolarReport(monthlySolarReport_id));
    return apiClient
      .patch(`/monthlysolarreports/${monthlySolarReport_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, monthlySolarReportSchema))
      .then(json => dispatch(receiveMonthlySolarReports(json.entities.monthlySolarReports)))
      .catch(error => dispatch(genericErrorAction(error, EDIT_MONTHLY_SOLAR_REPORT)));
  };
}

/* Create Actions */
export const CREATE_MONTHLY_SOLAR_REPORT = 'CREATE_MONTHLY_SOLAR_REPORT';
function sayCreateMonthlySolarReport(values) {
  return {
    type: CREATE_MONTHLY_SOLAR_REPORT,
    values: values,
  };
}

export function createMonthlySolarReport(values) {
  return dispatch => {
    dispatch(sayCreateMonthlySolarReport(values));
    return apiClient
      .post(`/monthlysolarreports/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, monthlySolarReportSchema))
      .then(json => dispatch(receiveMonthlySolarReports(json.entities.monthlySolarReports)))
      .catch(error => dispatch(genericErrorAction(error, CREATE_MONTHLY_SOLAR_REPORT)));
  };
}

/* Delete Actions */
export const DELETE_MONTHLY_SOLAR_REPORT = 'DELETE_MONTHLY_SOLAR_REPORT';
function sayDeleteMonthlySolarReport(monthlySolarReport_id) {
  return {
    type: DELETE_MONTHLY_SOLAR_REPORT,
    monthlySolarReport: monthlySolarReport_id,
  };
}

export function deleteMonthlySolarReport(monthlySolarReport_id) {
  return dispatch =>
    apiClient
      .delete(`/monthlysolarreports/${monthlySolarReport_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteMonthlySolarReport(monthlySolarReport_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_MONTHLY_SOLAR_REPORT)));
}
